import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { GameAction, GameState, Move, Square } from '@ccc/shared-types';
import { clockMsForDisplay } from '@ccc/rule-engine';
import { ChessBoard } from '../components/ChessBoard';
import { Panel } from '../components/Card';
import { CardTile } from '../components/CardTile';
import { Button } from '../components/Button';
import { NoScroll } from '../components/Layout';
import { useLocalGame, cardById } from '../lib/localGame';

export function GameRoute() {
  const { state, engine, draft, pickCard, attemptMove, attemptAction, claimTimeout, reset } =
    useLocalGame();
  const now = useNow();

  useEffect(() => {
    if (!state || state.status !== 'active') return;
    if (clockMsForDisplay(state, state.clocks.ticking ?? state.turn, Date.now()) <= 0) claimTimeout();
  }, [claimTimeout, now, state]);

  const legalMovesFor = useMemo(() => {
    if (!state || !engine) return undefined;
    return (sq: Square): Move[] => {
      const piece = Object.values(state.pieces).find((p) => p.square === sq);
      if (!piece) return [];
      return engine.generateLegalMovesForPiece(piece, state);
    };
  }, [state, engine]);

  if (!state && !draft) {
    return (
      <div className="h-full w-full grid place-items-center">
        <NoScroll />
        <Panel className="p-8 text-center">
          <p className="text-ink-muted">No game in progress.</p>
          <Link to="/play/bot" className="inline-block mt-4">
            <Button>Start a bot game</Button>
          </Link>
        </Panel>
      </div>
    );
  }

  if (draft) {
    return (
      <div className="h-full w-full px-5 md:px-10 py-6 grid grid-rows-[auto_1fr] gap-5 overflow-hidden">
        <NoScroll />
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold">Pick your rule</h1>
          <p className="text-sm text-ink-muted">
            One of three. Active for the whole game. Your opponent has their own three.
          </p>
        </div>
        <div className="min-h-0 flex items-start justify-center gap-5 flex-wrap">
          {draft.whiteOffer.map((cid) => {
            const c = cardById(cid)!;
            return (
              <CardTile
                key={cid}
                card={c}
                rarity={
                  c.category === 'general'
                    ? 'rare'
                    : c.category === 'king' || c.category === 'queen'
                    ? 'legendary'
                    : 'common'
                }
                selected={draft.whitePicked === cid}
                onPick={() => !draft.whitePicked && pickCard('white', cid)}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (!state) return null;
  const whiteCard = state.activeRules.white ? cardById(state.activeRules.white) : null;
  const blackCard = state.activeRules.black ? cardById(state.activeRules.black) : null;
  const activeSide = state.pendingBonus?.side ?? state.turn;
  const canStoreTempo = Boolean(
    engine?.generateLegalActions(state, 'white').some((action) => action.kind === 'store_tempo'),
  );

  return (
    <div className="h-full w-full max-w-[1400px] mx-auto px-3 md:px-10 py-3 md:py-5 flex flex-col lg:grid lg:grid-cols-[1fr_minmax(0,min(640px,calc(100svh-9rem)))_1fr] lg:grid-rows-[minmax(0,1fr)] gap-3 md:gap-5 overflow-hidden">
      <NoScroll />
      <div className="grid grid-cols-2 gap-2 lg:hidden shrink-0">
        <Panel className="p-3">
          <CompactSlot label="Opponent" card={blackCard} clockMs={clockMsForDisplay(state, 'black', now)} />
        </Panel>
        <Panel className="p-3">
          <CompactSlot label="You" card={whiteCard} clockMs={clockMsForDisplay(state, 'white', now)} />
        </Panel>
      </div>

      <aside className="hidden lg:flex flex-col gap-4 justify-end min-h-0 overflow-hidden">
        <Panel className="p-4">
          <Slot label="Opponent" color="black" card={blackCard} clockMs={clockMsForDisplay(state, 'black', now)} />
        </Panel>
      </aside>

      <div className="grid place-items-center min-h-0 flex-1 lg:flex-none overflow-hidden">
        <div
          className="aspect-square"
          style={{ width: 'min(100%, calc(100svh - 9rem))' }}
        >
          <ChessBoard
            state={state}
            playableSide={activeSide === 'white' ? 'white' : null}
            legalMovesFor={legalMovesFor}
            onMove={(m) => {
              attemptMove(m);
            }}
          />
        </div>
      </div>

      <aside className="hidden lg:flex flex-col gap-4 min-h-0 overflow-hidden">
        <Panel className="p-4 shrink-0">
          <Slot label="You" color="white" card={whiteCard} clockMs={clockMsForDisplay(state, 'white', now)} />
        </Panel>
        <Panel className="p-4 shrink-0">
          <div className="text-xs uppercase tracking-wider text-ink-muted">Move</div>
          <div className="font-display text-2xl font-bold">{state.moveNumber}</div>
          <div className="text-xs text-ink-muted mt-1">
            Turn: <span className="font-medium text-ink capitalize">{activeSide}</span>
          </div>
          <EffectBlock
            state={state}
            canStoreTempo={canStoreTempo}
            onStoreTempo={() => attemptAction({ kind: 'store_tempo', side: 'white' })}
          />
        </Panel>
        <Panel className="p-4 min-h-0 flex flex-col">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-2 shrink-0">Moves</h3>
          <div className="min-h-0 flex-1 overflow-y-auto text-sm font-mono text-ink-muted">
            <MoveHistory state={state} />
          </div>
        </Panel>
        <div className="flex gap-2 shrink-0">
          <Button variant="secondary" onClick={() => reset()}>
            Resign
          </Button>
        </div>
        <BugLink gameId={state.id} />
        {state.status === 'ended' && (
          <Panel className="p-4 ring-2 ring-accent shrink-0">
            <div className="font-display text-xl font-bold">Game over</div>
            <div className="text-sm text-ink-muted">
              {state.winner === 'draw' ? 'Drawn.' : `${state.winner} wins. (${state.endReason})`}
            </div>
            <Link to="/play/bot" onClick={() => reset()} className="inline-block mt-3">
              <Button>Play again</Button>
            </Link>
          </Panel>
        )}
      </aside>

      <div className="lg:hidden shrink-0">
        <Panel className="p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-ink-muted">
              Move <span className="font-mono text-ink">{state.moveNumber}</span> · Turn{' '}
              <span className="capitalize text-ink">{activeSide}</span>
            </div>
            <Button variant="secondary" size="sm" onClick={() => reset()}>
              Resign
            </Button>
          </div>
          <EffectBlock
            state={state}
            canStoreTempo={canStoreTempo}
            onStoreTempo={() => attemptAction({ kind: 'store_tempo', side: 'white' })}
          />
          <BugLink gameId={state.id} compact />
        </Panel>
      </div>
    </div>
  );
}

function Slot({
  label,
  color,
  card,
  clockMs,
}: {
  label: string;
  color: 'white' | 'black';
  card: ReturnType<typeof cardById> | null | undefined;
  clockMs: number;
}) {
  const seconds = Math.max(0, Math.floor(clockMs / 1000));
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
        <div className="font-mono text-lg font-medium">
          {mm}:{ss.toString().padStart(2, '0')}
        </div>
      </div>
      <div className="text-sm font-semibold mt-1 capitalize">{color}</div>
      {card ? (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{card.category}</div>
          <div className="font-display font-semibold text-ink">{card.shortTitle}</div>
          <p className="mt-1 text-xs text-ink-muted">{card.description}</p>
        </div>
      ) : (
        <div className="mt-3 text-xs text-ink-muted">No card selected.</div>
      )}
    </div>
  );
}

function CompactSlot({
  label,
  card,
  clockMs,
}: {
  label: string;
  card: ReturnType<typeof cardById> | null | undefined;
  clockMs: number;
}) {
  const seconds = Math.max(0, Math.floor(clockMs / 1000));
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider text-ink-muted">{label}</div>
        <div className="font-mono text-base font-semibold tabular-nums">
          {Math.floor(seconds / 60)}:{(seconds % 60).toString().padStart(2, '0')}
        </div>
      </div>
      <div className="mt-1 truncate font-display text-sm font-semibold">
        {card?.shortTitle ?? 'No card'}
      </div>
    </div>
  );
}

export function EffectBlock({
  state,
  canStoreTempo,
  onStoreTempo,
}: {
  state: GameState;
  canStoreTempo: boolean;
  onStoreTempo: () => void;
}) {
  const whiteTempo = Number(state.ruleState['stored-tempo-white'] ?? 0);
  const blackTempo = Number(state.ruleState['stored-tempo-black'] ?? 0);
  const castleMaster =
    state.activeRules.white === 'general-castle-master' ||
    state.activeRules.black === 'general-castle-master';
  const hasEffect = canStoreTempo || state.pendingBonus || whiteTempo || blackTempo || castleMaster;
  if (!hasEffect) return null;
  return (
    <div className="mt-3 rounded-md border border-ink/10 bg-ink/[0.03] p-2 text-xs text-ink-muted">
      {state.pendingBonus && (
        <div>
          Bonus: <span className="font-medium text-ink">{bonusLabel(state.pendingBonus.kind)}</span>
        </div>
      )}
      {(whiteTempo || blackTempo) && (
        <div>
          Tempo W {whiteTempo.toFixed(1)} · B {blackTempo.toFixed(1)}
        </div>
      )}
      {castleMaster && <div>Castle Master lanes open when the path is clear.</div>}
      {canStoreTempo && (
        <Button size="sm" variant="secondary" className="mt-2 w-full" onClick={onStoreTempo}>
          Store Tempo
        </Button>
      )}
    </div>
  );
}

export function MoveHistory({ state }: { state: GameState }) {
  const actions: GameAction[] = state.actionHistory?.length
    ? state.actionHistory
    : state.history.map((move) => ({ kind: 'move', move }));
  if (actions.length === 0) return <div>—</div>;
  return (
    <>
      {actions.map((action, i) => (
        <div key={i}>{actionLabel(action, i)}</div>
      ))}
    </>
  );
}

function actionLabel(action: GameAction, index: number): string {
  if (action.kind === 'store_tempo') {
    return `${Math.floor(index / 2) + 1}. ${action.side} stores tempo`;
  }
  const move = action.move;
  return `${Math.floor(index / 2) + 1}.${index % 2 ? '..' : ''} ${move.from}→${move.to}${
    move.special ? ` (${move.special})` : ''
  }`;
}

function bonusLabel(kind: string): string {
  return kind.replaceAll('_', ' ');
}

function BugLink({ gameId, compact }: { gameId: string; compact?: boolean }) {
  return (
    <a
      href={`/support?type=bug&gameId=${encodeURIComponent(gameId)}`}
      target="_blank"
      rel="noreferrer"
      className={`block text-xs text-ink-muted hover:text-accent focus-ring rounded-md ${
        compact ? 'mt-2' : ''
      }`}
    >
      New site. Found a bug? Send it here.
    </a>
  );
}

function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);
  return now;
}
